import { memo, type ReactNode, useEffect, useRef } from 'react';

import { useBreakpointsContext } from '@air/provider-media-query';
import { useToasts } from '@air/provider-toast';

import { AnnotationV2Provider } from '~/components/AnnotationsV2/providers/AnnotationV2Provider';
import { DiscussionsPanelContextProvider } from '~/components/AssetModal/shared/context/DiscussionsPanelContext/DiscussionsPanelContext';
import { FullscreenContextProvider } from '~/components/AssetModal/shared/context/FullscreenContext';
import { AssetModalPanelsContextProvider } from '~/components/AssetModalV2/AssetModalPanelsContextProvider';
import { PDFOutlineProvider } from '~/components/AssetModalV2/Content/Footer/contexts/PDFOutlineContext';
import { PDFZoomProvider } from '~/components/AssetModalV2/Content/Footer/contexts/PDFZoomContext';
import { ImageSearchContextProvider } from '~/components/AssetModalV2/Content/Header/contexts/ImageSearchContextProvider';
import { PDFSearchBridgeContextProvider } from '~/components/AssetModalV2/Content/Header/contexts/PDFSearchBridgeContextProvider';
import { ImageContentControlsContextProvider } from '~/components/AssetModalV2/Content/ImageContentControlsContextProvider';
import { PeoplePanelContextProvider } from '~/components/AssetModalV2/Content/Panel/contexts/PeoplePanelContextProvider';
import { PDFContentControlsContextProvider } from '~/components/AssetModalV2/Content/PDFContentControlsContextProvider';
import { CollapsibleSectionsContextProvider } from '~/components/AssetModalV2/InfoPanel/CollapsibleSectionsContextProvider';
import { FullscreenContextProvider as FullscreenContextProviderV2 } from '~/components/AssetModalV2/shared/context/FullscreenContext';
import {
  AssetModalStoreContextProvider,
  useAssetModalStore,
} from '~/components/v2/components/asset-modal/AssetModalStoreContext';
import { ZustandStorageKeys } from '~/constants/zustandStorageKeys';
import { AssetModalPanelContextProvider } from '~/providers/AssetModalPanelContextProvider';
import { VideoTimestampObserverProvider } from '~/providers/VideoTimestampObserverProvider';

/**
 * The zustand store is the one composition AssetModal genuinely owns: the component mounts its
 * store provider itself, so mounting AssetModal without a store id is a TYPE error rather than a
 * runtime guard throw. Every other per-surface concern (close, headers, annotation composition)
 * rides the `desktop` / `mobile` trees' own props — it lives with the trees that consume it, and
 * the modal stays surface-blind.
 */
export type AssetModalProps = {
  desktop: ReactNode;
  mobile: ReactNode;
  /**
   * Keys the modal's zustand store instance (panel sizes, theme, …) — the open clip's id (the
   * public shell falls back to the shortId before the clip resolves). The localStorage persistence
   * slot is AssetModal's own concern and never varies, so it is not a prop.
   */
  storeId: string;
};

type AssetModalBodyProps = Pick<AssetModalProps, 'desktop' | 'mobile'>;

// The store read (theme) must sit BELOW the store provider AssetModal itself mounts — hence the
// inner body component.
const AssetModalBody = memo(({ desktop, mobile }: AssetModalBodyProps) => {
  const theme = useAssetModalStore((state) => state.theme);
  const { isAboveMediumScreen } = useBreakpointsContext();
  const { setToastXOffset, getToastXOffset } = useToasts();
  const previousOffset = useRef(getToastXOffset());

  useEffect(() => {
    const previousValue = previousOffset.current;
    setToastXOffset(0);

    return () => {
      setToastXOffset(previousValue);
    };
  }, [setToastXOffset]);

  return (
    <div
      // Mobile UI is on UI1 which doesn't fully support dark mode
      data-theme={isAboveMediumScreen ? theme : 'dark'}
    >
      <AssetModalPanelContextProvider>
        <AssetModalPanelsContextProvider>
          <ImageSearchContextProvider>
            <PeoplePanelContextProvider>
              <PDFSearchBridgeContextProvider>
                <PDFZoomProvider>
                  <PDFOutlineProvider>
                    <PDFContentControlsContextProvider>
                      <VideoTimestampObserverProvider>
                        <ImageContentControlsContextProvider>
                          <CollapsibleSectionsContextProvider>
                            <DiscussionsPanelContextProvider>
                              <FullscreenContextProvider>
                                <FullscreenContextProviderV2>
                                  <AnnotationV2Provider>{!isAboveMediumScreen ? mobile : desktop}</AnnotationV2Provider>
                                </FullscreenContextProviderV2>
                              </FullscreenContextProvider>
                            </DiscussionsPanelContextProvider>
                          </CollapsibleSectionsContextProvider>
                        </ImageContentControlsContextProvider>
                      </VideoTimestampObserverProvider>
                    </PDFContentControlsContextProvider>
                  </PDFOutlineProvider>
                </PDFZoomProvider>
              </PDFSearchBridgeContextProvider>
            </PeoplePanelContextProvider>
          </ImageSearchContextProvider>
        </AssetModalPanelsContextProvider>
      </AssetModalPanelContextProvider>
    </div>
  );
});

AssetModalBody.displayName = 'AssetModalBody';

export const AssetModal = memo(({ desktop, mobile, storeId }: AssetModalProps) => (
  <AssetModalStoreContextProvider id={storeId} storageKey={ZustandStorageKeys.AssetModal}>
    <AssetModalBody desktop={desktop} mobile={mobile} />
  </AssetModalStoreContextProvider>
));

AssetModal.displayName = 'AssetModal';
